import type { Rewards } from '@morpho-org/dapp-core'

import { safeParseNumber } from '@morpho-org/blue-sdk-viem'

import type { VaultV2ApyBreakdown } from '@/lib/procedures/vaultV2Apy.contract'

import { formatMaxApy, formatVaultApy } from './formatters'

export interface VaultApyCardRowsParams {
  breakdown: VaultV2ApyBreakdown | null
  netApyExcludingRewards: number | null
  netApy?: number | null
  rewards: readonly number[]
  includesDonations: boolean
  isRateCapped: boolean
  maxApy: number | null
}

export interface VaultApyCardRows {
  native: number | null
  boost: { label: 'Donations' | 'Surplus boost'; value: number } | null
  rewards: number[]
  /** WAD-scaled row values rounded to 0.01% so they sum to the displayed Net APY. */
  displayed: { native: bigint | null; boost: bigint | null; rewards: bigint[] }
  netApy: number | null
  capCopy: string | null
  hasSparkle: boolean
  hasCapIcon: boolean
  hasPausedIcon: boolean
  hasCard: boolean
}

/**
 * Whether an APY decimal fits the card's 0.01% integer grid. The bridge clamps an infinite APY to
 * `Number.MAX_VALUE`, which would overflow to `Infinity` there and make `BigInt` throw.
 */
const isApyInRange = (value: number) =>
  Number.isFinite(value) && Math.abs(value) * 10_000 <= Number.MAX_SAFE_INTEGER

/** Whether every value of a breakdown fits the card's grid, so it can be reconciled. */
export const isApyBreakdownInRange = (breakdown: NonNullable<VaultV2ApyBreakdown>) =>
  [
    breakdown.apy,
    breakdown.organicApy,
    breakdown.surplusBoostApy,
    breakdown.netApyExcludingRewards,
    breakdown.organicNetApyExcludingRewards,
    breakdown.surplusBoostNetApyExcludingRewards
  ].every(value => value === null || isApyInRange(value))

/**
 * The breakdown when the card can split the row with it: a known organic net APY and every value in
 * range. Otherwise `null`, so the row falls back to the top-level APY values.
 */
export const toUsableApyBreakdown = (breakdown: VaultV2ApyBreakdown | null | undefined) =>
  breakdown?.organicNetApyExcludingRewards != null && isApyBreakdownInRange(breakdown)
    ? breakdown
    : null

const getFloorAndRemainder = (value: number) => {
  const scaled = Number((value * 10_000).toPrecision(12))
  const floor = Math.floor(scaled)

  return { floor, remainder: scaled - floor }
}

const hundredthsToWad = (hundredths: number) => BigInt(hundredths) * 10n ** 14n

const getDisplayedRows = (
  native: number | null,
  boost: VaultApyCardRows['boost'],
  rewards: number[],
  netApy: number | null
): VaultApyCardRows['displayed'] => {
  const rowValues = [native, boost?.value ?? null, ...rewards]
  const flooredRows = rowValues.map(value =>
    value === null || !isApyInRange(value) ? null : getFloorAndRemainder(value)
  )
  const hundredths = flooredRows.map(row => row?.floor ?? 0)
  const toWadAt = (index: number) => hundredthsToWad(hundredths[index] ?? 0)

  if (native === null || netApy === null) {
    return {
      native: null,
      boost: null,
      rewards: rewards.map((_, index) => toWadAt(index + 2))
    }
  }

  if (Math.abs(netApy) < 10) {
    const formattedNetApy = formatVaultApy(netApy)
    const target = Number(
      `${formattedNetApy.startsWith('-') ? '-' : ''}${formattedNetApy.replace(/[^\d]/g, '')}`
    )
    const leftover = target - hundredths.reduce((sum, value) => sum + value, 0)

    if (leftover !== 0) {
      const adjustmentOrder = flooredRows
        .flatMap((row, index) => (row === null ? [] : [index]))
        .sort((left, right) => {
          const remainderDifference = flooredRows[left]!.remainder - flooredRows[right]!.remainder

          return (leftover > 0 ? -remainderDifference : remainderDifference) || left - right
        })
      const adjustment = Math.sign(leftover)

      for (const index of adjustmentOrder.slice(
        0,
        Math.min(Math.abs(leftover), adjustmentOrder.length)
      )) {
        hundredths[index] = (hundredths[index] ?? 0) + adjustment
      }
    }
  }

  return {
    native: toWadAt(0),
    boost: boost === null ? null : toWadAt(1),
    rewards: rewards.map((_, index) => toWadAt(index + 2))
  }
}

export const mapVaultApyCardRows = ({
  breakdown,
  netApyExcludingRewards,
  netApy: procedureNetApy,
  rewards,
  includesDonations,
  isRateCapped,
  maxApy
}: VaultApyCardRowsParams): VaultApyCardRows => {
  const usableBreakdown = toUsableApyBreakdown(breakdown)
  const native = usableBreakdown
    ? usableBreakdown.organicNetApyExcludingRewards
    : netApyExcludingRewards !== null && isApyInRange(netApyExcludingRewards)
      ? netApyExcludingRewards
      : null
  const boostLabel: 'Donations' | 'Surplus boost' = includesDonations
    ? 'Donations'
    : 'Surplus boost'
  const boostValue = usableBreakdown?.surplusBoostNetApyExcludingRewards
  const boost =
    boostValue != null && boostValue > 0
      ? {
          label: boostLabel,
          value: boostValue
        }
      : null
  const rewardRows = [...rewards]
  const hasRowsInRange = rewardRows.every(isApyInRange)
  const hasPausedIcon = maxApy === 0
  const hasCapIcon = isRateCapped && !hasPausedIcon
  const hasSparkle = rewardRows.length > 0 || (boost?.value ?? 0) > 0
  const hasCard = hasSparkle || hasCapIcon || hasPausedIcon

  let capCopy: string | null = null
  if (hasCapIcon) {
    const formattedCap = formatMaxApy(maxApy)
    if (formattedCap) {
      const firstSentence = boost
        ? `Native APY and ${boost.label} combined are capped at ${formattedCap}.`
        : `Native APY is capped at ${formattedCap}.`
      capCopy = [
        firstSentence,
        ...(rewardRows.length ? ["Rewards aren't capped."] : []),
        'Yield above the cap is paid out over time.'
      ].join(' ')
    }
  }

  const summedNetApy =
    native === null || !hasRowsInRange
      ? null
      : Number(
          (
            native +
            (boost?.value ?? 0) +
            rewardRows.reduce((sum, reward) => sum + reward, 0)
          ).toPrecision(15)
        )
  const netApy =
    summedNetApy !== null &&
    procedureNetApy != null &&
    Math.abs(procedureNetApy - summedNetApy) <= 1e-12
      ? procedureNetApy
      : summedNetApy

  const displayed = getDisplayedRows(native, boost, rewardRows, netApy)

  return {
    native,
    boost,
    rewards: rewardRows,
    displayed,
    netApy,
    capCopy,
    hasSparkle,
    hasCapIcon,
    hasPausedIcon,
    hasCard
  }
}

export interface VaultRowApyCardRowsParams {
  breakdown: VaultV2ApyBreakdown | null
  /** WAD-scaled native APY, net of fees and excluding rewards. */
  apy: bigint | null | undefined
  /** WAD-scaled net APY. */
  netApy?: bigint | null
  rewards: readonly Rewards[] | null | undefined
  includesDonations?: boolean
  isRateCapped?: boolean
  maxApy?: number | null
}

/**
 * {@link mapVaultApyCardRows} from a row's WAD-scaled values, as the APY card reads them.
 * Without a usable breakdown, the sparkle falls back to rewards or the donation allowlist.
 */
export const mapVaultRowApyCardRows = ({
  breakdown,
  apy,
  netApy,
  rewards,
  includesDonations,
  isRateCapped,
  maxApy
}: VaultRowApyCardRowsParams): VaultApyCardRows => {
  const rows = mapVaultApyCardRows({
    breakdown,
    netApyExcludingRewards: apy == null ? null : Number(apy) / 1e18,
    netApy: netApy == null ? null : Number(netApy) / 1e18,
    rewards: (rewards ?? []).map(reward => Number(reward.apr ?? 0n) / 1e18),
    includesDonations: includesDonations ?? false,
    isRateCapped: isRateCapped ?? false,
    maxApy: maxApy ?? null
  })
  if (toUsableApyBreakdown(breakdown)) return rows

  const hasSparkle = !!rewards?.length || !!includesDonations
  return {
    ...rows,
    hasSparkle,
    hasCard: hasSparkle || rows.hasCapIcon || rows.hasPausedIcon
  }
}

/**
 * The WAD-scaled net APY a row displays: the headline its APY card reconciles from a usable
 * breakdown. Without one, `null`, so the row keeps the API net APY.
 */
export const getVaultRowDisplayedNetApy = (
  rows: Pick<VaultApyCardRows, 'netApy'> | null,
  breakdown: VaultV2ApyBreakdown | null | undefined
) =>
  !toUsableApyBreakdown(breakdown) || rows?.netApy == null ? null : safeParseNumber(rows.netApy)
